import axios from "axios";
import React, { useRef, useEffect, useState } from "react";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";

function CountryOfOriginField({
  value,
  onChange,
  name = "countryOfOrigin",
  required = false,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<any>) => void;
  name?: string;
  required?: boolean;
}) {
  return (
    <div className="form-group">
      <label>Country Of Origin{required && <span className="text-danger"> *</span>}</label>
      <select name={name} className="form-input compact-select" value={value} onChange={onChange}>
        <option value="">Select country</option>
        <option value="None">None</option>
        <option value="International">International</option>
        <option value="Canada">Canada</option>
        <option value="Mexico">Mexico</option>
        <option value="Poland">Poland</option>
        <option value="Matricula">Matricula</option>
        <option value="Other">Other</option>
      </select>
    </div>
  );
}


function LicenseStatusField({
  value,
  onChange,
  name = "licenseStatus",
  state,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<any>) => void;
  name?: string;
  state?: string;
}) {
  // Unified license status options for all states
  const LICENSE_STATUS_ALABAMA = ["Valid", "Expired", "Revoked", "Suspended"];
  const LICENSE_STATUS_OHIO = ["Valid", "Unlicensed", "Permit", "Suspended"];
  const LICENSE_STATUS_FLORIDA = ["Valid", "Unlicensed", "Permit", "Expired", "Revoked", "Suspended"];
  const LICENSE_STATUS_GEORGIA = ["Valid", "Unlicensed", "Permit", "Expired", "Revoked", "Suspended"];
  const LICENSE_STATUS_TENNESSEE = ["Valid", "Unlicensed", "Permit", "Suspended"];
  const LICENSE_STATUS_SOUTH_CAROLINA = ["Valid", "Unlicensed", "Permit", "Expired", "Revoked", "Suspended"];
  const LICENSE_STATUS_DEFAULT = ["Valid", "Unlicensed", "Permit", "Suspended", "Revoked", "Expired", "Foreign", "Restricted"];

  const licenseStatuses =
    state === "Ohio"
      ? LICENSE_STATUS_OHIO
      : state === "Alabama"
        ? LICENSE_STATUS_ALABAMA
        : state === "Florida"
          ? LICENSE_STATUS_FLORIDA
          : state === "Georgia"
            ? LICENSE_STATUS_GEORGIA
            : state === "Tennessee"
              ? LICENSE_STATUS_TENNESSEE
              : state === "South Carolina"
                ? LICENSE_STATUS_SOUTH_CAROLINA
                : LICENSE_STATUS_DEFAULT;
  return (
    <div className="form-group">
      <label>License Status</label>
      <select name={name} className="form-input compact-select" value={value} onChange={onChange}>
        <option value="">Select status</option>
        {licenseStatuses.map((status) => (
          <option key={status} value={status}>
            {status}
          </option>
        ))}
      </select>
    </div>
  );
}

function InternationalDriversLicenseField({
  value,
  onChange,
  name = "internationalDriversLicense",
}: {
  value: string;
  onChange: (e: React.ChangeEvent<any>) => void;
  name?: string;
}) {
  return (
    <div className="form-group">
      <label>International Driver's License</label>
      <select name={name} className="form-input compact-select" value={value} onChange={onChange}>
        <option value="">Select</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    </div>
  );
}

function MonthsEmployedField({
  value,
  onChange,
  name = "monthsEmployed",
}: {
  value: string | number;
  onChange: (e: React.ChangeEvent<any>) => void;
  name?: string;
}) {
  return (
    <div className="form-group">
      <label>Months Employed</label>
      <input
        type="number"
        name={name}
        className="form-input"
        value={value}
        onChange={onChange}
        min={0}
        max={11}
        step={1}
        placeholder="0–11"
        onKeyDown={(e) => [".", "e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
      />
    </div>
  );
}

const EDUCATION_OPTIONS_UNIFIED = [
  "None",
  "Bachelors Degree",
  "Masters Degree",
  "Doctorate Degree",
  "Medical Degree",
  "Law Degree",
  "No High School Diploma",
  "High School Diploma",
  "Associates Degree",
  "Some College",
  "Vocational Degree",
];

const occupationOptions = [
  { name: "Accountant/Auditor", value: "Accountant/Auditor" },
  { name: "Administrative Assistant", value: "Administrative Assistant" },
  { name: "Agent", value: "Agent" },
  { name: "Agent/Broker", value: "Agent/Broker" },
  { name: "Assistant-Medic/Dent/Vet", value: "Assistant-Medic/Dent/Vet" },
  { name: "Attorney", value: "Attorney" },
  { name: "Bartender", value: "Bartender" },
  { name: "Bookkeeper", value: "Bookkeeper" },
  { name: "Caregiver", value: "Caregiver" },
  { name: "Cashier/Checker", value: "Cashier/Checker" },
  { name: "Child/Daycare Worker", value: "Child/Daycare Worker" },
  { name: "Claims Adjuster", value: "Claims Adjuster" },
  { name: "Clergy", value: "Clergy" },
  { name: "Clerk-Office", value: "Clerk-Office" },
  { name: "Client Care Worker", value: "Client Care Worker" },
  { name: "Construction Worker", value: "Construction Worker" },
  { name: "Construction-Project Manager", value: "Construction-Project Manager" },
  { name: "Contractor", value: "Contractor" },
  { name: "Cook/Worker-Fast Food", value: "Cook/Worker-Fast Food" },
  { name: "Cook-Restaurant/Cafeteria", value: "Cook-Restaurant/Cafeteria" },
  { name: "Corrections Officer", value: "Corrections Officer" },
  { name: "CSR/Teller", value: "CSR/Teller" },
  { name: "Custodian/Janitor", value: "Custodian/Janitor" },
  { name: "Customer Service Representative", value: "Customer Service Representative" },
  { name: "Delivery Person", value: "Delivery Person" },
  { name: "Director/Administrator", value: "Director/Administrator" },
  { name: "Doctor", value: "Doctor" },
  { name: "Driver-Truck/Delivery", value: "Driver-Truck/Delivery" },
  { name: "Engineer", value: "Engineer" },
  { name: "Enlisted Military Personnel(E1-E4)", value: "Enlisted Military Personnel(E1-E4)" },
  { name: "Executive", value: "Executive" },
  { name: "Factory Worker", value: "Factory Worker" },
  { name: "Fire Fighter/Supervisor", value: "Fire Fighter/Supervisor" },
  { name: "Foreman/Supervisor", value: "Foreman/Supervisor" },
  { name: "Hair Stylist/Barber", value: "Hair Stylist/Barber" },
  { name: "Handyman", value: "Handyman" },
  { name: "Heat/Air Technician", value: "Heat/Air Technician" },
  { name: "Homemaker(full-time)", value: "Homemaker(full-time)" },
  { name: "Housekeeper/Maid", value: "Housekeeper/Maid" },
  { name: "Laborer/Worker", value: "Laborer/Worker" },
  { name: "Manager", value: "Manager" },
  { name: "Mechanic", value: "Mechanic" },
  { name: "Nurse-C.N.A.", value: "Nurse-C.N.A." },
  { name: "Nurse-RN", value: "Nurse-RN" },
  { name: "Officer-Commissioned", value: "Officer-Commissioned" },
  { name: "Officer-Warrant", value: "Officer-Warrant" },
  { name: "Other", value: "Other" },
  { name: "Painter", value: "Painter" },
  { name: "Pilot/Captain/Engineer", value: "Pilot/Captain/Engineer" },
  { name: "Plumber", value: "Plumber" },
  { name: "Police Detective/Investigator", value: "Police Detective/Investigator" },
  { name: "Police Officer/Supervisor", value: "Police Officer/Supervisor" },
  { name: "Principal", value: "Principal" },
  { name: "Professor College", value: "Professor College" },
  { name: "Programmer", value: "Programmer" },
  { name: "Realtor", value: "Realtor" },
  { name: "Receptionist/Secretary", value: "Receptionist/Secretary" },
  { name: "Retired(full-time)", value: "Retired(full-time)" },
  { name: "Roofer", value: "Roofer" },
  { name: "Sales Agent/Representative", value: "Sales Agent/Representative" },
  { name: "Sales Representative", value: "Sales Representative" },
  { name: "Sales-Homebased", value: "Sales-Homebased" },
  { name: "Sales-Retail/Wholesale", value: "Sales-Retail/Wholesale" },
  { name: "Student(full-time)", value: "Student(full-time)" },
  { name: "Support Services", value: "Support Services" },
  { name: "Teacher College", value: "Teacher College" },
  { name: "Teacher K-12", value: "Teacher K-12" },
  { name: "Underwriter", value: "Underwriter" },
  { name: "Unemployed", value: "Unemployed" },
  { name: "US Postal Worker", value: "US Postal Worker" },
  { name: "Waiter/Waitress", value: "Waiter/Waitress" },
  { name: "Web Developer", value: "Web Developer" },
  { name: "Welder", value: "Welder" },
];

const LICENSE_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

const YES_NO_OPTIONS = ["Yes", "No"];

const SR22_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

export default function DriverInformationConfig({
  driverConfigurationList,
  state,
  getDriverConfigurationDetails,
  loading,
}: {
  driverConfigurationList: any;
  state: string;
  getDriverConfigurationDetails: () => void;
  loading: boolean;
}) {
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = React.useState({
    education: "",
    occupation: "",
    relationship: "",
    driverType: "",
    licenseState: "",
    sR22: "",
    sR22State: "",
    previousClaims: "",
    countryOfOrigin: "",
    licenseStatus: "",
    internationalDriversLicense: "",
    monthsEmployed: "",
    yearsEmployed: "",
    milesToWork: "",
    annualMiles: "",
  });

  // SR-22 State mapping for specific states

  const effectiveSR22State = formData.sR22State || state;
  const toast = React.useRef<Toast>(null);
  const navigate = useNavigate();
  const [saveLoading, setSaveLoading] = useState(false);

  const handleReset = () => {
    setFormData({
      education: "",
      occupation: "",
      relationship: "",
      driverType: "",
      licenseState: "",
      sR22: "",
      sR22State: "",
      previousClaims: "",
      countryOfOrigin: "",
      licenseStatus: "",
      internationalDriversLicense: "",
      monthsEmployed: "",
      yearsEmployed: "",
      milesToWork: "",
      annualMiles: "",
    });
  };

  useEffect(() => {
    if (driverConfigurationList) {
      setFormData({
        education: driverConfigurationList.education ?? "",
        occupation: driverConfigurationList.occupation ?? "",
        relationship: driverConfigurationList.relationship ?? "",
        driverType: driverConfigurationList.driverType ?? "",
        licenseState: driverConfigurationList.licenseState ?? "",
        sR22: driverConfigurationList.sR22 ?? "",
        sR22State: driverConfigurationList.sR22State ?? "",
        previousClaims: driverConfigurationList.previousClaims ?? "",
        countryOfOrigin: driverConfigurationList.countryOfOrigin ?? "",
        licenseStatus: driverConfigurationList.licenseStatus ?? "",
        internationalDriversLicense: driverConfigurationList.internationalDriversLicense ?? "",
        monthsEmployed: driverConfigurationList.monthsEmployed ?? "",
        yearsEmployed: driverConfigurationList.yearsEmployed ?? "",
        milesToWork: driverConfigurationList.milesToWork ?? "",
        annualMiles: driverConfigurationList.annualMiles ?? "",
      });
    }
  }, [driverConfigurationList]);
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    let clamped = value;
    if (name === "milesToWork" && value !== "") {
      const n = Math.floor(Number(value));
      clamped = String(Math.min(999, Math.max(0, n)));
    } else if (name === "annualMiles" && value !== "") {
      const n = Math.floor(Number(value));
      clamped = String(Math.min(1000000, Math.max(0, n)));
    } else if (name === "yearsEmployed" && value !== "") {
      const n = Math.floor(Number(value));
      clamped = String(Math.min(99, Math.max(0, n)));
    } else if (name === "monthsEmployed" && value !== "") {
      const n = Math.floor(Number(value));
      clamped = String(Math.min(11, Math.max(0, n)));
    }
    setFormData((prev) => ({
      ...prev,
      [name]: clamped,
    }));
  };
  const onDriverConfigurationSaveClick = () => {
    if (!state || state.trim() === "") {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "State is required.",
        life: 3000,
      });
      return;
    }

    // Validate Country of Origin is required when International Drivers License is Yes
    if (formData.internationalDriversLicense === "Yes" && !formData.countryOfOrigin) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Country of Origin is required when International Driver's License is Yes.",
        life: 3000,
      });
      return;
    }

    setSaveLoading(true);
    let data = JSON.stringify({
      education: formData.education,
      occupation: formData.occupation,
      relationship: formData.relationship,
      driverType: formData.driverType,
      licenseState: formData.licenseState,
      previousClaims: formData.previousClaims,
      sR22: formData.sR22,
      sR22State: formData.sR22State,
      countryOfOrigin: formData.countryOfOrigin,
      licenseStatus: formData.licenseStatus,
      internationalDriversLicense: formData.internationalDriversLicense,
      monthsEmployed: formData.monthsEmployed,
      yearsEmployed: formData.yearsEmployed,
      milesToWork: formData.milesToWork,
      annualMiles: formData.annualMiles,
      state: state,
    });

    var config = {
      method: "post",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/driverinformationconfiguration`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "application/json",
      },
      data: data,
    };

    axios(config)
      .then((response: any) => {
        setSaveLoading(false);
        getDriverConfigurationDetails();
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Driver configurations saved successfully!",
          life: 3000,
        });
      })
      .catch((error: any) => {
        setSaveLoading(false);
        if (error.response && error.response.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response && error.response.status
                ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while saving the driver information configurations!"}`
                : "Unknown error while saving the driver information configurations!",
            life: 3000,
          });
        }
      });
  };
  return (
    <div>
      <Toast ref={toast} />

      <div className="config-info-banner">
        <div className="config-info-icon">
          <svg viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <line x1="12" y1="8" x2="12" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M12 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <div className="config-info-text">
          <p className="feature-bold">Driver Information Settings</p>
          <span className="feature-label">Configure driver-related defaults below.</span>
        </div>
      </div>
      <div style={{ position: "relative" }}>
        {loading && (
          <div className="driver-loading-overlay">
            <div className="driver-loading-content">
              <div className="driver-loading-spinner" />
              <p className="driver-loading-text">Loading driver configuration...</p>
            </div>
          </div>
        )}
        <form ref={formRef}>
          <div
            className="form-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(220px, 1fr))",
              gap: "1rem",
            }}
          >
            <div className="form-group">
              <label>Driver Type</label>
              <select name="driverType" className="form-input compact-select" value={formData.driverType} onChange={handleChange}>
                <option value="">Select type</option>
                <option value="Rated">Rated</option>
                <option value="Excluded">Excluded</option>
              </select>
            </div>

            <div className="form-group">
              <label>Education</label>
              <select name="education" className="form-input compact-select" value={formData.education} onChange={handleChange}>
                <option value="">Select education</option>

                {EDUCATION_OPTIONS_UNIFIED.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            {/* Occupation */}
            <div className="form-group">
              <label>Occupation</label>
              <select name="occupation" className="form-input compact-select" value={formData.occupation} onChange={handleChange}>
                <option value="">Select occupation</option>

                {occupationOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>
          

            {/* Years Employed */}
            <div className="form-group">
              <label>Years Employed</label>
              <input
                type="number"
                name="yearsEmployed"
                className="form-input"
                value={formData.yearsEmployed}
                onChange={handleChange}
                min={0}
                max={99}
                step={1}
                placeholder="0–99"
                onKeyDown={(e) => [".", "e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
              />
            </div>

            {/* Months Employed */}
            <MonthsEmployedField value={formData.monthsEmployed} onChange={handleChange} />
  {/* Miles to Work */}
            <div className="form-group">
              <label>Miles to Work</label>
              <input
                type="number"
                name="milesToWork"
                className="form-input"
                value={formData.milesToWork}
                onChange={handleChange}
                min={0}
                max={999}
                step={1}
                placeholder="0–999"
                onKeyDown={(e) => [".", "e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
              />
            </div>

            {/* Annual Miles */}
            <div className="form-group">
              <label>Annual Miles</label>
              <input
                type="number"
                name="annualMiles"
                className="form-input"
                value={formData.annualMiles}
                onChange={handleChange}
                min={0}
                max={1000000}
                step={1}
                placeholder="0–1000000"
                onKeyDown={(e) => [".", "e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
              />
            </div>
            {/* License State */}
            <div className="form-group">
              <label>License State</label>
              <select name="licenseState" className="form-input compact-select" value={formData.licenseState} onChange={handleChange}>
                <option value="">Select state</option>
                {LICENSE_STATES.map((opt) => (
                  <option key={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <LicenseStatusField value={formData.licenseStatus} onChange={handleChange} state={state} />

            {/* International Drivers License - Available for all states */}
            <InternationalDriversLicenseField value={formData.internationalDriversLicense} onChange={handleChange} />

            {/* Country of Origin - Available for all states, required when International Drivers License is Yes */}
            <CountryOfOriginField
              value={formData.countryOfOrigin}
              onChange={handleChange}
              required={formData.internationalDriversLicense === "Yes"}
            />
            {/* Claims */}
            <div className="form-group">
              <label>Any Accidents or Violations in the Previous 3 Years</label>
              <select name="previousClaims" className="form-input compact-select" value={formData.previousClaims} onChange={handleChange}>
                <option value="">Select</option>
                {[...YES_NO_OPTIONS].map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>SR22</label>
              <select name="sR22" className="form-input compact-select" value={formData.sR22} onChange={handleChange}>
                <option value="">Select</option>
                {YES_NO_OPTIONS.map((opt) => (
                  <option key={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {/* SR22 State */}
            <div className="form-group">
              <label>SR22 State</label>
              <select name="sR22State" className="form-input compact-select" value={effectiveSR22State} onChange={handleChange}>
                <option value="">Select state</option>
                {SR22_STATES.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            {/* Relationship */}
            <div className="form-group">
              <label>Relationship</label>
              <select name="relationship" className="form-input compact-select" value={formData.relationship} onChange={handleChange}>
                <option value="">Select relationship</option>
                <option value="Insured">Insured</option>
                <option value="Spouse">Spouse</option>
                <option value="Child">Child</option>
                <option value="Other Related">Other Related</option>
                <option value="Other Non Related">Other Non Related</option>
                <option value="Parent">Parent</option>
              </select>
            </div>
          </div>
          <div className="tab-actions">
            <button type="button" className="btn btn-primary" onClick={onDriverConfigurationSaveClick} disabled={saveLoading}>
              {saveLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
